import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, addDoc, serverTimestamp, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import './LoyaltyProgram.css';

const LoyaltyProgram = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, customers, rewards, settings

  const [customers, setCustomers] = useState([]);
  const [loyaltyTiers, setLoyaltyTiers] = useState([
    { name: 'Bronze', minPoints: 0, maxPoints: 499, discount: 5, color: '#cd7f32' },
    { name: 'Silver', minPoints: 500, maxPoints: 1499, discount: 10, color: '#c0c0c0' },
    { name: 'Gold', minPoints: 1500, maxPoints: 4999, discount: 15, color: '#ffd700' },
    { name: 'Platinum', minPoints: 5000, maxPoints: Infinity, discount: 20, color: '#e5e4e2' }
  ]);

  const [programStats, setProgramStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalPointsIssued: 0,
    totalPointsRedeemed: 0,
    avgPointsPerCustomer: 0,
    redemptionRate: 0
  });

  const [rewardsConfig, setRewardsConfig] = useState({
    pointsPerRupee: 1, // 1 point per ₹1 spent
    signupBonus: 100,
    birthdayBonus: 200,
    referralBonus: 500,
    minRedemption: 100,
    pointExpiry: 365 // days
  });

  const [showAddReward, setShowAddReward] = useState(false);
  const [newReward, setNewReward] = useState({
    name: '',
    pointsCost: 0,
    description: '',
    type: 'discount', // discount, freeItem, voucher
    value: 0,
    validityDays: 30
  });

  useEffect(() => {
    fetchLoyaltyData();
  }, []);

  const fetchLoyaltyData = async () => {
    try {
      setLoading(true);

      // Fetch customers with loyalty data
      const customersQuery = query(collection(db, 'customers'), orderBy('loyaltyPoints', 'desc'));
      const customersSnapshot = await getDocs(customersQuery);
      const customersData = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        loyaltyPoints: doc.data().loyaltyPoints || 0,
        tier: calculateTier(doc.data().loyaltyPoints || 0)
      }));
      setCustomers(customersData);

      // Calculate program statistics
      const totalPoints = customersData.reduce((sum, c) => sum + (c.loyaltyPoints || 0), 0);
      const totalRedeemed = customersData.reduce((sum, c) => sum + (c.pointsRedeemed || 0), 0);
      const activeCustomers = customersData.filter(c => {
        const lastOrder = c.lastOrderDate?.toDate?.() || new Date(c.lastOrderDate || 0);
        const daysSince = (Date.now() - lastOrder.getTime()) / (1000 * 60 * 60 * 24);
        return daysSince <= 90; // Active = ordered in last 90 days
      });

      setProgramStats({
        totalMembers: customersData.length,
        activeMembers: activeCustomers.length,
        totalPointsIssued: totalPoints + totalRedeemed,
        totalPointsRedeemed: totalRedeemed,
        avgPointsPerCustomer: customersData.length > 0 ? Math.round(totalPoints / customersData.length) : 0,
        redemptionRate: totalPoints + totalRedeemed > 0 ? ((totalRedeemed / (totalPoints + totalRedeemed)) * 100).toFixed(1) : 0
      });

      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching loyalty data:', error);
      setLoading(false);
    }
  };

  const calculateTier = (points) => {
    for (let i = loyaltyTiers.length - 1; i >= 0; i--) {
      if (points >= loyaltyTiers[i].minPoints) {
        return loyaltyTiers[i];
      }
    }
    return loyaltyTiers[0];
  };

  const getTierDistribution = () => {
    const distribution = {};
    loyaltyTiers.forEach(tier => {
      distribution[tier.name] = customers.filter(c => c.tier.name === tier.name).length;
    });
    return distribution;
  };

  const handleAdjustPoints = async (customerId, adjustment, reason) => {
    try {
      const customer = customers.find(c => c.id === customerId);
      const newPoints = Math.max(0, (customer.loyaltyPoints || 0) + adjustment);

      await updateDoc(doc(db, 'customers', customerId), {
        loyaltyPoints: newPoints,
        lastPointsUpdate: serverTimestamp()
      });

      // Log the adjustment
      await addDoc(collection(db, 'loyalty_transactions'), {
        customerId,
        customerName: customer.name,
        type: adjustment > 0 ? 'earned' : 'redeemed',
        points: Math.abs(adjustment),
        reason,
        timestamp: serverTimestamp()
      });

      alert(`Successfully ${adjustment > 0 ? 'added' : 'deducted'} ${Math.abs(adjustment)} points`);
      fetchLoyaltyData();
    } catch (error) {
      console.error('❌ Error adjusting points:', error);
      alert('Failed to adjust points');
    }
  };

  const handleAddReward = async () => {
    try {
      if (!newReward.name || !newReward.pointsCost) {
        alert('Please fill in reward name and points cost');
        return;
      }

      await addDoc(collection(db, 'loyalty_rewards'), {
        ...newReward,
        active: true,
        createdAt: serverTimestamp()
      });

      alert('Reward added successfully!');
      setShowAddReward(false);
      setNewReward({
        name: '',
        pointsCost: 0,
        description: '',
        type: 'discount',
        value: 0,
        validityDays: 30
      });
    } catch (error) {
      console.error('❌ Error adding reward:', error);
      alert('Failed to add reward');
    }
  };

  const handleSaveConfig = async () => {
    try {
      // Save to settings collection
      const settingsRef = doc(db, 'settings', 'loyalty_config');
      await updateDoc(settingsRef, {
        ...rewardsConfig,
        updatedAt: serverTimestamp()
      }).catch(async () => {
        // If document doesn't exist, create it
        await addDoc(collection(db, 'settings'), {
          id: 'loyalty_config',
          ...rewardsConfig,
          createdAt: serverTimestamp()
        });
      });

      alert('Loyalty program configuration saved!');
    } catch (error) {
      console.error('❌ Error saving config:', error);
      alert('Failed to save configuration');
    }
  };

  if (loading) {
    return (
      <div className="loyalty-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading loyalty program data...</p>
        </div>
      </div>
    );
  }

  const tierDistribution = getTierDistribution();

  return (
    <div className="loyalty-container">
      <div className="loyalty-header">
        <h1>🎁 Loyalty & Rewards Program</h1>
        <p>Manage customer loyalty points, rewards, and program settings</p>
      </div>

      {/* Tab Navigation */}
      <div className="loyalty-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={activeTab === 'customers' ? 'active' : ''}
          onClick={() => setActiveTab('customers')}
        >
          👥 Customers
        </button>
        <button
          className={activeTab === 'rewards' ? 'active' : ''}
          onClick={() => setActiveTab('rewards')}
        >
          🎁 Rewards
        </button>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="loyalty-content">
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <div className="stat-label">Total Members</div>
                <div className="stat-value">{programStats.totalMembers}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✨</div>
              <div className="stat-info">
                <div className="stat-label">Active Members</div>
                <div className="stat-value">{programStats.activeMembers}</div>
                <div className="stat-sub">Last 90 days</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-info">
                <div className="stat-label">Points Issued</div>
                <div className="stat-value">{programStats.totalPointsIssued.toLocaleString()}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💎</div>
              <div className="stat-info">
                <div className="stat-label">Points Redeemed</div>
                <div className="stat-value">{programStats.totalPointsRedeemed.toLocaleString()}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-info">
                <div className="stat-label">Avg Points/Customer</div>
                <div className="stat-value">{programStats.avgPointsPerCustomer}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔄</div>
              <div className="stat-info">
                <div className="stat-label">Redemption Rate</div>
                <div className="stat-value">{programStats.redemptionRate}%</div>
              </div>
            </div>
          </div>

          {/* Loyalty Tiers */}
          <div className="tiers-section">
            <h2>🏆 Loyalty Tiers</h2>
            <div className="tiers-grid">
              {loyaltyTiers.map(tier => (
                <div
                  key={tier.name}
                  className="tier-card"
                  style={{ borderColor: tier.color }}
                >
                  <div className="tier-header" style={{ backgroundColor: tier.color }}>
                    <h3>{tier.name}</h3>
                  </div>
                  <div className="tier-body">
                    <div className="tier-range">
                      {tier.minPoints === 0 ? '0' : tier.minPoints}
                      {tier.maxPoints === Infinity ? '+' : ` - ${tier.maxPoints}`} points
                    </div>
                    <div className="tier-benefit">
                      <strong>{tier.discount}% discount</strong>
                    </div>
                    <div className="tier-count">
                      {tierDistribution[tier.name] || 0} members
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Customers */}
          <div className="top-customers-section">
            <h2>⭐ Top Loyalty Members</h2>
            <table className="customers-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Customer</th>
                  <th>Tier</th>
                  <th>Points</th>
                  <th>Total Spent</th>
                  <th>Join Date</th>
                </tr>
              </thead>
              <tbody>
                {customers.slice(0, 10).map((customer, index) => (
                  <tr key={customer.id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="customer-info">
                        <strong>{customer.name}</strong>
                        <span>{customer.phone}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className="tier-badge"
                        style={{ backgroundColor: customer.tier.color }}
                      >
                        {customer.tier.name}
                      </span>
                    </td>
                    <td><strong>{customer.loyaltyPoints}</strong></td>
                    <td>₹{(customer.totalSpent || 0).toLocaleString()}</td>
                    <td>
                      {customer.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="loyalty-content">
          <div className="customers-list">
            <h2>👥 All Loyalty Customers</h2>
            <table className="customers-table full">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Tier</th>
                  <th>Points</th>
                  <th>Redeemed</th>
                  <th>Total Orders</th>
                  <th>Total Spent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(customer => (
                  <tr key={customer.id}>
                    <td>
                      <div className="customer-info">
                        <strong>{customer.name}</strong>
                        <span>{customer.phone}</span>
                        <span>{customer.email}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className="tier-badge"
                        style={{ backgroundColor: customer.tier.color }}
                      >
                        {customer.tier.name}
                      </span>
                    </td>
                    <td><strong>{customer.loyaltyPoints}</strong></td>
                    <td>{customer.pointsRedeemed || 0}</td>
                    <td>{customer.orderCount || 0}</td>
                    <td>₹{(customer.totalSpent || 0).toLocaleString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-small primary"
                          onClick={() => {
                            const points = parseInt(prompt('Enter points to add:'));
                            if (points) {
                              handleAdjustPoints(customer.id, points, 'Manual adjustment');
                            }
                          }}
                        >
                          + Add
                        </button>
                        <button
                          className="btn-small danger"
                          onClick={() => {
                            const points = parseInt(prompt('Enter points to deduct:'));
                            if (points) {
                              handleAdjustPoints(customer.id, -points, 'Manual deduction');
                            }
                          }}
                        >
                          - Deduct
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rewards Tab */}
      {activeTab === 'rewards' && (
        <div className="loyalty-content">
          <div className="rewards-header">
            <h2>🎁 Available Rewards</h2>
            <button
              className="btn-primary"
              onClick={() => setShowAddReward(true)}
            >
              + Add New Reward
            </button>
          </div>

          {showAddReward && (
            <div className="add-reward-modal">
              <div className="modal-content">
                <h3>Add New Reward</h3>
                <div className="form-group">
                  <label>Reward Name</label>
                  <input
                    type="text"
                    value={newReward.name}
                    onChange={(e) => setNewReward({ ...newReward, name: e.target.value })}
                    placeholder="e.g., 10% Off Next Order"
                  />
                </div>
                <div className="form-group">
                  <label>Points Cost</label>
                  <input
                    type="number"
                    value={newReward.pointsCost}
                    onChange={(e) => setNewReward({ ...newReward, pointsCost: parseInt(e.target.value) })}
                    placeholder="e.g., 500"
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={newReward.type}
                    onChange={(e) => setNewReward({ ...newReward, type: e.target.value })}
                  >
                    <option value="discount">Discount</option>
                    <option value="freeItem">Free Item</option>
                    <option value="voucher">Voucher</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Value</label>
                  <input
                    type="number"
                    value={newReward.value}
                    onChange={(e) => setNewReward({ ...newReward, value: parseInt(e.target.value) })}
                    placeholder="e.g., 10 (for 10% or ₹10)"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newReward.description}
                    onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
                    placeholder="Describe the reward..."
                  />
                </div>
                <div className="form-group">
                  <label>Validity (Days)</label>
                  <input
                    type="number"
                    value={newReward.validityDays}
                    onChange={(e) => setNewReward({ ...newReward, validityDays: parseInt(e.target.value) })}
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={handleAddReward}>
                    Add Reward
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowAddReward(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="rewards-grid">
            <div className="reward-card sample">
              <h3>10% Off Next Order</h3>
              <div className="reward-cost">500 points</div>
              <p>Get 10% discount on your next order</p>
              <div className="reward-meta">Valid for 30 days</div>
            </div>
            <div className="reward-card sample">
              <h3>Free Dessert</h3>
              <div className="reward-cost">300 points</div>
              <p>Redeem for any dessert from our menu</p>
              <div className="reward-meta">Valid for 15 days</div>
            </div>
            <div className="reward-card sample">
              <h3>₹200 Voucher</h3>
              <div className="reward-cost">1000 points</div>
              <p>₹200 off on orders above ₹500</p>
              <div className="reward-meta">Valid for 60 days</div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="loyalty-content">
          <div className="settings-section">
            <h2>⚙️ Loyalty Program Settings</h2>
            <div className="settings-form">
              <div className="form-group">
                <label>Points Per Rupee Spent</label>
                <input
                  type="number"
                  value={rewardsConfig.pointsPerRupee}
                  onChange={(e) => setRewardsConfig({ ...rewardsConfig, pointsPerRupee: parseFloat(e.target.value) })}
                />
                <span className="help-text">
                  Example: 1 point per ₹1 spent
                </span>
              </div>
              <div className="form-group">
                <label>Signup Bonus Points</label>
                <input
                  type="number"
                  value={rewardsConfig.signupBonus}
                  onChange={(e) => setRewardsConfig({ ...rewardsConfig, signupBonus: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Birthday Bonus Points</label>
                <input
                  type="number"
                  value={rewardsConfig.birthdayBonus}
                  onChange={(e) => setRewardsConfig({ ...rewardsConfig, birthdayBonus: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Referral Bonus Points</label>
                <input
                  type="number"
                  value={rewardsConfig.referralBonus}
                  onChange={(e) => setRewardsConfig({ ...rewardsConfig, referralBonus: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Minimum Points for Redemption</label>
                <input
                  type="number"
                  value={rewardsConfig.minRedemption}
                  onChange={(e) => setRewardsConfig({ ...rewardsConfig, minRedemption: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Points Expiry (Days)</label>
                <input
                  type="number"
                  value={rewardsConfig.pointExpiry}
                  onChange={(e) => setRewardsConfig({ ...rewardsConfig, pointExpiry: parseInt(e.target.value) })}
                />
              </div>
              <button className="btn-primary" onClick={handleSaveConfig}>
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyProgram;
